/**
 * One approve or execute command against a plan step, and what is known about it afterwards
 * (cq-039, LEA-169).
 *
 * `applied` comes from the route's own answer, or, when that answer was unclear, from a recorded
 * event carrying this command's ID for this step: an approval's `step.approved`, a queue's
 * `step.queued` (`src/conversation-events.ts`, whose command events cite the command ID as
 * evidence). An applied event proves the command was accepted, not that the provider finished.
 * `rejected` is a 400 or 409 naming why. Anything else is `unknown`.
 *
 * A newer plan revision is not a receipt: another rep, a new message or a redraft moves it too, so
 * the plan is never read here. Nothing is retried and no command ID is made here; a caller that
 * asks again passes the same ID, which the server answers from its command ledger.
 */
import { z } from "zod";

type PlanCommandAction = "approve" | "execute";

export interface PlanCommand {
  readonly stepId: string;
  readonly action: PlanCommandAction;
  readonly commandId: string;
  readonly expectedRevision: number;
}

export type PlanCommandOutcome =
  /** `revision` is the plan's after the command; null when an event, not the answer, proved it. */
  | { readonly kind: "applied"; readonly revision: number | null }
  | { readonly kind: "rejected"; readonly status: 400 | 409; readonly reason: string | null }
  | { readonly kind: "unknown" };

export interface PlanCommandPorts {
  /** Posts the command body to the plan's command route; rejects when no answer came back. */
  readonly send: (body: PlanCommand) => Promise<Response>;
  /** The conversation's recorded events (`GET …/events`), read only after an unclear answer. */
  readonly events: () => Promise<unknown>;
}

/** The browser's ports: the plan's command route, same-origin, and the page's own events read. */
export function planCommandPorts(
  planApi: string,
  events: () => Promise<unknown>,
): PlanCommandPorts {
  return {
    send: (body) =>
      fetch(`${planApi}/commands`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
    events,
  };
}

const AppliedSchema = z.object({ plan: z.object({ revision: z.number().int().nonnegative() }) });
const RefusalSchema = z.object({ error: z.string() });
const RecordedSchema = z.object({
  events: z.array(
    z.object({
      kind: z.string(),
      evidence: z.object({ kind: z.string(), ref: z.string() }),
      detail: z.object({ stepId: z.string().optional() }),
    }),
  ),
});

const PROVING_EVENT: Readonly<Record<PlanCommandAction, string>> = {
  approve: "step.approved",
  execute: "step.queued",
};

export async function issuePlanCommand(
  ports: PlanCommandPorts,
  command: PlanCommand,
): Promise<PlanCommandOutcome> {
  let response: Response;
  try {
    response = await ports.send(command);
  } catch {
    return recorded(ports, command);
  }
  if (response.ok) {
    const applied = AppliedSchema.safeParse(await response.json().catch(() => null));
    return applied.success
      ? { kind: "applied", revision: applied.data.plan.revision }
      : recorded(ports, command);
  }
  if (response.status === 400 || response.status === 409) {
    const refusal = RefusalSchema.safeParse(await response.json().catch(() => null));
    return {
      kind: "rejected",
      status: response.status,
      reason: refusal.success ? refusal.data.error : null,
    };
  }
  return recorded(ports, command);
}

/** Whether the record holds this very command on this step; unknown when it does not or cannot say. */
async function recorded(
  ports: PlanCommandPorts,
  command: PlanCommand,
): Promise<PlanCommandOutcome> {
  const read = RecordedSchema.safeParse(await ports.events().catch(() => null));
  if (!read.success) return { kind: "unknown" };
  const proof = read.data.events.some(
    (event) =>
      event.kind === PROVING_EVENT[command.action] &&
      event.evidence.kind === "command" &&
      event.evidence.ref === command.commandId &&
      event.detail.stepId === command.stepId,
  );
  return proof ? { kind: "applied", revision: null } : { kind: "unknown" };
}
